/**
 * Updated for new UI design (v2)
 * Changes made:
 * 1. Previously, `TransactionAccordion` was called for each code within a transaction.
 *    Now, it is called for each entire transaction.
 * 2. Modified the arguments passed to `TransactionAccordion` to align with the new implementation.
 * 3. Removed code related to 'ZippedCodeBlock'.
 * 4. Replaced the previous accordion implementation with Chakra UI's Accordion component to render each charge.
 */

import React from 'react';
import { AccordionSectionAction } from '@codametrix/ui-components';
import classnames from 'classnames';
import {
  cmxTypeguards,
  commonEnums,
  dragManager,
  caseUtils
} from '@codametrix/ui-common';
import { canDropOnto, CODE_ORDER } from './can-drop';
import { isAValidUnits, parseUnits } from './transaction-charge.utils';
import { CHARGE_TYPE_LABEL } from '../../code-bench-panel/invoice-meta/invoice-meta-info';
import ErrorSubmission from '../../code-bench-panel/error-submission';

import { TransactionProps, TransactionState } from './transaction.types';
import { CMXIcon } from '@codametrix/ui-components';
import TimeBasedCodePopover from './time-based-code-popover';
import {
  Popover,
  PopoverTrigger,
  PopoverArrow,
  PopoverContent,
  PopoverBody,
  Portal,
  Button
} from '@chakra-ui/react';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box
} from '@chakra-ui/react';
import { transactionAccordianBuilderV2 } from './transaction-accordionV2';
import { medicalCodeAccordianBuilder } from './medical-code-accordion';
import { SuggestedLookup } from '../../code-bench-panel/lookup/lookupV2';
import { getLedgerTransaction, getLedgerTransactionsFromInvoice, shouldHighlightTransaction } from '../../../utils/utils';

const { ChargeSessionCodeProvenance, CodeSearchType } = commonEnums;

const draggedCodesManager = dragManager.load('codes');

const sharedCodePredicate = (code: CMxCommonApp.ChargeSessionCode) => {
  //@ts-ignore
  return code.shared === true;
};

const notSharedCodePredicate = (code: CMxCommonApp.ChargeSessionCode) => {
  //@ts-ignore
  return !(code.shared === true);
};

/**
 * Method that get data from codable object
 * @param element
 * @param codable
 * @returns
 */
const getDataItem = (
  element: HTMLElement,
  codable: CMxCommonApp.ChargeSession | CMxCommonApp.CodableTransaction
) => {
  const parent = element.closest('.transaction-headerV2');
  if (parent) {
    const codeId = parent.attributes.getNamedItem('data-code');
    const codeType = parent.attributes.getNamedItem('data-code-type');

    if (codeId && codeType) {
      return (
        codable.codes.find((code) => {
          if (!cmxTypeguards.isChargeSessionCode(code)) return false;
          const sessionCode = code as CMxCommonApp.ChargeSessionCode;
          return (
            `${sessionCode.code}` === `${codeId.value}` &&
            sessionCode.codeType === `${codeType.value}`
          );
        }) ?? null
      );
    }
  }
  return null;
};

/**
 * Method that get data from codable object
 * @param element
 * @param codable
 * @returns
 */
const getChipDataItem = (
  element: HTMLElement,
  codeInfo: CMxCommonApp.CodeInfo,
  codable: CMxCommonApp.ChargeSession | CMxCommonApp.CodableTransaction
) => {
  const chipElement = !!element ? element?.closest('.medical-code-chip') : null;

  const canProceed = !!chipElement || !!codeInfo;
  if (canProceed) {
    const codeId = !!codeInfo
      ? codeInfo?.code
      : chipElement?.attributes?.getNamedItem('data-code')?.value;
    const codeType = caseUtils.getCodeTypeFullname(
      !!codeInfo
        ? codeInfo?.codeType
        : chipElement?.attributes?.getNamedItem('data-code-type')?.value
    );
    if (codeId && codeType) {
      return (
        codable.codes.find((code) => {
          if (!cmxTypeguards.isChargeSessionCode(code)) return false;
          const sessionCode = code as CMxCommonApp.ChargeSessionCode;
          return (
            `${sessionCode.code}` === `${codeId}` &&
            sessionCode.codeType === `${codeType}`
          );
        }) ?? null
      );
    }
  }
  return null;
};

/**
 * Class React component for render a transaction
 */
class TransactionV2 extends React.Component<
  TransactionProps,
  TransactionState
> {
  static displayName = 'Transaction';
  static defaultProps = {
    autoFocus: false,
    searchResults: [],
    codeSearchResultsV2: {},
    editable: true,
    panelActivations: {},
    codeEdits: {},
    debounceTimeout: 200,
    suggestedCodes: {
      cpt: [],
      modifier: [],
      diagnosis: []
    },
    canDropOnto: canDropOnto,
    onChargeSessionAction: (
      transactionAction: CMxCommonApp.TransactionAction
    ) => {},
    onTransactionSharedDiagnosisChange: (
      changeDetail: CMxCommonApp.TransactionSharedCodeChange
    ) => {},
    handleCodeChange: (change: CMxCommonApp.CodeChange) => {},
    handleCodeRemove: (change: CMxCommonApp.CodeChange) => {},
    handleCodeSearchDrop: (change: CMxCommonApp.CodeChange) => {},
    onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => {},
    onCodeSearchSelection: (
      searchSelection: Accelerate.CodeActionPayload
    ) => {},
    handleAddAllCodes: (searchSelection: Accelerate.CodesAddCodeBench) => {},
    handleRemoveAllCodes: (codeChange: CMxCommonApp.CodeChange) => {},
    onFeatureSelection: (feature: CMxCommonApp.ChargeSessionSelection) => {},
    onEdit: (chargeSession: CMxCommonApp.ChargeSession) => {},
    handleTimeBasedCodeAdd: (
      change: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {},
    handleTimeBasedCodeUpdate: (
      change: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {},
    handleTransactionReorder: (
      transactionCurrentIndex: number,
      transactionDestinationIndex: number
    ) => {}
  };

  lookupRef: React.RefObject<SuggestedLookup>;
  accordionPanelRef = React.createRef<HTMLDivElement>();
  triggerRef = React.createRef<HTMLButtonElement>();
  observer: IntersectionObserver | null = null;

  currentUnit: number;

  constructor(props: TransactionProps) {
    super(props);
    this.handleUnitsChange = this.handleUnitsChange.bind(this);
    this.handleTransactionReorder = this.handleTransactionReorder.bind(this);
    this.handleCodeDelete = this.handleCodeDelete.bind(this);
    this.handleCodesDelete = this.handleCodesDelete.bind(this);
    this.onSearch = this.onSearch.bind(this);
    this.onSearchSelection = this.onSearchSelection.bind(this);
    this.onAddAllCodes = this.onAddAllCodes.bind(this);
    this.onRemoveAllCodes = this.onRemoveAllCodes.bind(this);
    this.onDropHandler = this.onDropHandler.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onAccordionAction = this.onAccordionAction.bind(this);
    this.handleCodeChecked = this.handleCodeChecked.bind(this);
    this.handleEnableTechPro = this.handleEnableTechPro.bind(this);
    this.handleDisableTechPro = this.handleDisableTechPro.bind(this);
    this.isTimeBasedCode = this.isTimeBasedCode.bind(this);
    this.timeBasedCodePopup = this.timeBasedCodePopup.bind(this);
    this.codeDropHandler = this.codeDropHandler.bind(this);

    this.lookupRef = React.createRef();
    this.state = {
      dragHover: false,
      clickTriggered: false, //is the popover open
      hasTimeOpened: false, //has the popover been opened before in this session
      isTransactionRowOpen: false,
      isTriggerInView: true, //is the popover trigger in the scroll area
      isHidingWhileOutOfView: false //is the trigger out of view and the popover needs to be hidden
    };
  }

  /****/
  // Watch for the Popover trigger to go out of view and close it
  /****/
  componentDidMount() {
    // Create an intersection observer to watch the trigger
    this.setupIntersectionObserver();
  }

  componentWillUnmount() {
    // Clean up the observer when component unmounts
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Set up the intersection observer
  setupIntersectionObserver() {
    // First, clean up any existing observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Don't proceed if there's no scroll container
    if (!this.props.scrollContainerRef?.current) {
      // Schedule another attempt if the ref isn't ready
      setTimeout(() => this.setupIntersectionObserver(), 100);
      return;
    }

    // Don't set up observer if trigger ref isn't available
    if (!this.triggerRef.current) {
      return; // We'll set it up when the ref becomes available
    }

    const options = {
      root: this.props.scrollContainerRef.current,
      rootMargin: '0px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const isInView = entry.isIntersecting;

        this.setState({ isTriggerInView: isInView });

        // If trigger scrolls out of view while popover is open, close it
        if (!isInView && this.state.clickTriggered) {
          this.setState({
            isHidingWhileOutOfView: true,
            clickTriggered: false
          });
        }

        // If trigger scrolls into view after it had been temp closed, open it again
        if (isInView && this.state.isHidingWhileOutOfView) {
          this.setState({
            isHidingWhileOutOfView: false,
            clickTriggered: true
          });
        }
      });
    }, options);

    // Start observing the trigger element
    this.observer.observe(this.triggerRef.current);
  }

  // Update the component after ref is attached
  componentDidUpdate(prevProps, prevState) {
    // Check if new procedure code was added
    const prevProcedure = prevProps.codableTransaction?.procedure;
    const newProcedure = this.props.codableTransaction?.procedure;
    const procedureCodeChanged =
      prevProcedure?.procedure_code !== newProcedure?.procedure_code;

    // Check if accordion was just opened
    const accordionJustOpened =
      !prevState.isTransactionRowOpen && this.state.isTransactionRowOpen;
    const scrollContainerChanged =
      prevProps.scrollContainerRef?.current !==
      this.props.scrollContainerRef?.current;

    // If procedure code changed, reset all popover-related states
    if (procedureCodeChanged) {
      this.setState({
        clickTriggered: false,
        hasTimeOpened: false,
        isTriggerInView: true,
        isHidingWhileOutOfView: false
      });
    }

    // This is commented out until we can control the auto open with a config
    // Handle auto-opening for time-based codes
    /* if (newProcedure && 
      !this.state.clickTriggered && 
      !this.state.hasTimeOpened &&
      newProcedure.time_based && 
      newProcedure.add_on_code_available && 
      !newProcedure.total_time) {
    
      // When we need to auto-open, give the DOM time to render first
      setTimeout(() => {
        this.setState({ 
          clickTriggered: true, 
          hasTimeOpened: true,
          isTriggerInView: true,
          isHidingWhileOutOfView: false
        }, () => {
          // After state is updated and component re-renders, set up the observer
          setTimeout(() => this.setupIntersectionObserver(), 50);
          });
        }, 100);
    }  */

    // Always ensure the observer is set up correctly after key changes
    if (
      accordionJustOpened ||
      scrollContainerChanged ||
      (this.state.clickTriggered && !prevState.clickTriggered)
    ) {
      setTimeout(() => this.setupIntersectionObserver(), 50);
    }
  }

  /**
   * Event for drag over
   * @param ev
   */
  onDragOver(ev: React.DragEvent) {
    const data = draggedCodesManager.get();
    const searchResult = cmxTypeguards.isSearchResult(data);

    if (searchResult) {
      const { codableTransaction, chargeSession } = this.props;
      const hasCPT = (codableTransaction || chargeSession).codes
        .filter(cmxTypeguards.isChargeSessionCode)
        .some((sessionCode: CMxCommonApp.ChargeSessionCode) => {
          return (
            sessionCode.provenance.toUpperCase() ===
            ChargeSessionCodeProvenance.CPT.toUpperCase()
          );
        });

      const allowed =
        data.codeType.toUpperCase() === ChargeSessionCodeProvenance.CPT
          ? !hasCPT
          : true;
      if (allowed) {
        ev.dataTransfer.dropEffect = 'copy';
        ev.preventDefault();
        if (!this.state.dragHover) {
          this.setState({ dragHover: true });
        }
      }
    }
  }

  /**
   * Event for drag leaves
   * @param ev
   */
  onDragLeave(ev: React.DragEvent) {
    if (this.state.dragHover) {
      this.setState({ dragHover: false });
    }
    ev.dataTransfer.dropEffect = 'none';
  }

  /**
   * Event for drag drops
   * @param ev
   */
  onDrop(ev: React.DragEvent) {
    const searchResult = draggedCodesManager.parseEvent(ev);
    draggedCodesManager.clear();

    const { codableTransaction } = this.props;
    if (codableTransaction) {
      const codes: CMxCommonApp.ChargeSessionCode[] = codableTransaction.codes
        .filter((code): code is CMxCommonApp.ChargeSessionCode => cmxTypeguards.isChargeSessionCode(code))
        .filter((sessionCode) => {
          return (
            sessionCode.codeType.toUpperCase() ===
            searchResult.codeType.toUpperCase()
          );
        });
      const result = {
        ...searchResult,
        context: {
          invoice: { ...this.props.invoice },
          transaction: { ...this.props.codableTransaction },
          chargeSession: { ...this.props.chargeSession },
          processInstance: { ...this.props.processInstance }
        }
      };
      codes.push(result);
      this.props.onCodeSearchSelection({
        ...searchResult,
        context: {
          chargeSession: { ...this.props.chargeSession }
        }
      });

      this.setState({ dragHover: false });
      if (this.lookupRef.current) {
        this.lookupRef.current.reset();
        this.lookupRef.current.focus();
      }
    }
  }

  /**
   * Event for handle drag drop
   * @param ev
   */
  onDropHandler(
    dropAction: CMxCommonApp.DroppedAction<CMxCommonApp.ChargeSessionCode>
  ) {
    const { chargeSession, codableTransaction } = this.props;
    const { dropCandidate, dropTarget } = dropAction;
    const sameCodeType =
      `${dropCandidate.codeType}`.toUpperCase() ===
      `${dropTarget.codeType}`.toUpperCase();

    const matchCodes = (sessionCode: CMxCommonApp.ChargeSessionCode) => {
      const matchType =
        `${sessionCode.codeType}`.toUpperCase() ===
        `${dropCandidate.codeType}`.toUpperCase();
      const matchProvenance =
        `${sessionCode.provenance}`.toUpperCase() ===
        `${dropCandidate.codeType}`.toUpperCase();
      if (matchProvenance || matchType) {
        return sessionCode.code !== dropCandidate.code;
      }
      return false;
    };
    // remove the code from our code list
    let codes: CMxCommonApp.ChargeSessionCode[] = (
      codableTransaction || chargeSession
    ).codes
      .filter(matchCodes)
      .filter(cmxTypeguards.isChargeSessionCode);

    const searchResult = cmxTypeguards.isSearchResult(dropCandidate);
    let dropCode = dropCandidate;

    if (searchResult) {
      dropCode = {
        code: dropCandidate.code,
        codeType: dropCandidate.codeType,
        description: dropCandidate.description
      } as CMxCommonApp.ChargeSessionCode;

      let targetPosition = codes.findIndex(
        (sessionCode) => sessionCode.code === dropTarget.code
      );
      if (dropAction.position === 'after') {
        targetPosition++;
      }
      codes.splice(targetPosition, 0, dropCode);

      if (this.lookupRef.current) {
        this.lookupRef.current.reset();
        this.lookupRef.current.focus();
      }
    } else {
      // if the codes are of the same code type
      // then a drop is just a reordering of the same codes
      // if a drop is on a different code type, then we need
      // to do something different.
      if (sameCodeType) {
        let targetCode = dropTarget.code;

        if (cmxTypeguards.isZippedCodeBlock(dropCode)) {
          const zippedDropCode = dropCode as unknown as CMxCommonApp.ZippedCodeBlock;
          const blockSet = new Set(zippedDropCode.codes.map((code) => code.code));
          const filteredArray = [...codes].filter(
            (code) => !blockSet.has(code.code)
          );

          let seekPosition = filteredArray.findIndex(
            (sessionCode) => sessionCode.code === dropTarget.code
          );

          if (dropAction.position === 'after') {
            seekPosition++;
          }

          filteredArray.splice(seekPosition, 0, ...zippedDropCode.codes);
          codes = filteredArray;

          // capturing the drag complete for Datadog Action
          // @ts-ignore
          window.DD_RUM.addAction('Drag (shared row moved)', {});
        } else {
          // if this is a shared code block then we need to rewind to the
          // front of the shared codes
          let codeIncrement = 1;

          if (cmxTypeguards.isZippedCodeBlock(dropTarget)) {
            const zippedDropTarget = dropTarget as unknown as CMxCommonApp.ZippedCodeBlock;
            const [firstCode] = zippedDropTarget.codes;
            targetCode = firstCode.code;
            codeIncrement = zippedDropTarget.codes.length;
          }

          let targetPosition = codes.findIndex(
            (sessionCode) => sessionCode.code === targetCode
          );
          if (dropAction.position === 'after') {
            targetPosition += codeIncrement;
          }
          codes.splice(targetPosition, 0, dropCandidate);
        }

        // capturing the drag complete for Datadog Action
        // @ts-ignore
        window.DD_RUM.addAction('Drag (any code row moved)', {});
      } else {
        if (cmxTypeguards.isZippedCodeBlock(dropCandidate)) {
          const zippedDropCandidate = dropCandidate as unknown as CMxCommonApp.ZippedCodeBlock;
          const blockSet = new Set(
            zippedDropCandidate.codes.map((code) => code.code)
          );
          const filteredArray = [...codes].filter(
            (code) => !blockSet.has(code.code)
          );

          let seekPosition = filteredArray.findIndex(
            (sessionCode) => sessionCode.code === dropTarget.code
          );

          if (dropAction.position === 'after') {
            seekPosition++;
          }

          filteredArray.splice(seekPosition, 0, ...zippedDropCandidate.codes);
          codes = filteredArray;

          // capturing the drag complete for Datadog Action
          // @ts-ignore
          window.DD_RUM.addAction('Drag (shared row moved to top)', {});
        } else {
          // there is a defined visual order of codes
          // PROCEDURES, MODIFIERS, DX
          // if this changes, then the order will need to be updated
          const dropIndex = CODE_ORDER.get(`${dropCandidate.codeType}`);
          const dropTargetIndex = CODE_ORDER.get(`${dropTarget.codeType}`);
          if (dropIndex < dropTargetIndex) {
            codes.push(dropCandidate);
          } else {
            codes.unshift(dropCandidate);
          }

          // capturing the drag complete for Datadog Action
          // @ts-ignore
          window.DD_RUM.addAction('Drag (invoice code row move to top)', {});
        }
      }
    }

    const changeDetail = {
      codes: codes,
      context: {
        chargeSession: { ...this.props.chargeSession },
        invoice: { ...this.props.invoice },
        transaction: { ...this.props.codableTransaction },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleCodeSearchDrop(changeDetail);
    this.setState({ dragHover: false });
  }

  codeDropHandler(codes: CMxCommonApp.ChargeSessionCode[]) {
    const changeDetail = {
      codes: codes,
      context: {
        chargeSession: { ...this.props.chargeSession },
        invoice: { ...this.props.invoice },
        transaction: { ...this.props.codableTransaction },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleCodeSearchDrop(changeDetail);
  }

  /**
   * Event launched when a search is performed
   * @param ev
   */
  onSearch(codeQuery: CMxCommonApp.CodeQuery) {
    this.props.onCodeSearch({
      ...codeQuery,
      context: {
        transaction: { ...this.props.codableTransaction },
        invoice: { ...this.props.invoice },
        chargeSession: { ...this.props.chargeSession },
        processInstance: { ...this.props.processInstance }
      }
    });
  }

  /**
   * If the popup is acting on a code that is already a SHARED diagnosis on this
   * non-shared transaction, route through the same per-transaction shared-code
   * toggle that the chip checkbox uses (txnSharedCodeAction). The default
   * codeAdd/codesRemove paths cannot flip the existing entry's `active` flag —
   * they only add/remove rows — so without this branch a deactivated shared
   * dx can't be re-activated from the popup.
   */
  private findSharedExistingDx(codeInfo: {
    code: string;
    codeType?: any;
  }): CMxCommonApp.ChargeSessionCode | undefined {
    // The SHARED_CODES panel uses a synthesized stub transaction whose codes
    // are all shared:true — but those should be managed via /codes/shared/v1
    // (the bulk thunks already branch on this), not the per-transaction
    // active-flag toggle. Only real transactions own the active-flag concept.
    if (this.props.invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES) {
      return undefined;
    }
    return this.props.codableTransaction.codes
      .filter((c): c is CMxCommonApp.ChargeSessionCode =>
        cmxTypeguards.isChargeSessionCode(c)
      )
      .find(
        (c) =>
          c.code === codeInfo.code &&
          c.provenance === codeInfo.codeType &&
          sharedCodePredicate(c)
      );
  }

  private dispatchSharedDxToggle(code: string, action: 'ADD' | 'REMOVE') {
    const { codableTransaction, processInstance } = this.props;
    let diagnoses;
    if (action === 'REMOVE') {
      diagnoses = [
        {
          diagnosis_code: code,
          shared: true
        }
      ];
    } else {
      const codes = codableTransaction.codes
        .filter((c): c is CMxCommonApp.ChargeSessionCode =>
          cmxTypeguards.isChargeSessionCode(c)
        )
        .filter((c) => c.codeType === 'diagnosis');
      diagnoses = codes.map((c, idx) => ({
        diagnosis_code: c.code,
        sequence_number: idx + 1,
        //@ts-ignore
        shared: c.shared,
        //@ts-ignore
        active: c.code === code ? true : c.active
      }));
    }
    this.props.onTransactionSharedDiagnosisChange({
      action,
      codeUpdate: {
        transactionGuid: codableTransaction.guid,
        processInstance,
        serviceLine: processInstance.serviceLine,
        diagnoses
      }
    });
  }

  /**
   * Event launched when a search selection is pressed
   * @param ev
   */
  onSearchSelection(searchResult: Accelerate.CodeAddActionPayload) {
    if (this.findSharedExistingDx(searchResult.codeInfo)) {
      this.dispatchSharedDxToggle(searchResult.codeInfo.code, 'ADD');
      return;
    }
    const present = this.props.codableTransaction.codes.findIndex(
      (code: CMxCommonApp.ChargeSessionCode) =>
        code.code === searchResult.codeInfo.code
    );
    const searchSelection: Accelerate.CodeAddActionPayload = {
      codeInfo: searchResult.codeInfo,
      additionalCodes: searchResult?.additionalCodes || [],
      context: {
        ...searchResult.context,
        defaultPrevented: present !== -1,
        invoice: { ...this.props.invoice },
        transaction: { ...this.props.codableTransaction },
        chargeSession: { ...this.props.chargeSession },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.onCodeSearchSelection(searchSelection);
  }

  onAddAllCodes(codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]) {
    const filteredResults = codes.filter((result) => {
      const present = this.props.codableTransaction.codes
        .filter((code): code is CMxCommonApp.ChargeSessionCode => cmxTypeguards.isChargeSessionCode(code))
        .some((sessionCode) => {
          return (
            sessionCode.code === result.code &&
            sessionCode.provenance === result.codeType
          );
        });

      // Keep the code if it is not existing code
      return !present;
    });

    const searchSelection = {
      codesInfo: filteredResults,
      context: {
        invoice: { ...this.props.invoice },
        transaction: { ...this.props.codableTransaction },
        chargeSession: { ...this.props.chargeSession },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleAddAllCodes(searchSelection);
  }

  onRemoveAllCodes(
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) {
    const { chargeSession, codableTransaction } = this.props;

    const sharedDx: typeof codes = [] as any;
    const others: typeof codes = [] as any;
    for (const result of codes) {
      if (this.findSharedExistingDx(result)) {
        (sharedDx as any).push(result);
      } else {
        (others as any).push(result);
      }
    }

    sharedDx.forEach((c) =>
      this.dispatchSharedDxToggle(c.code, 'REMOVE')
    );

    const codeList = [];
    const existingCodes = others.filter((result) => {
      const present = this.props.codableTransaction.codes
        .filter((code): code is CMxCommonApp.ChargeSessionCode => cmxTypeguards.isChargeSessionCode(code))
        .some((sessionCode) => {
          return (
            sessionCode.code === result.code &&
            sessionCode.provenance === result.codeType
          );
        });

      // Keep the code if it is existing code
      return present;
    });

    existingCodes.forEach((codeInfo) => {
      const chipDataItem = getChipDataItem(
        null,
        codeInfo,
        codableTransaction || chargeSession
      );
      if (chipDataItem) {
        codeList.push(codeInfo);
      }
    });
    if (codeList.length > 0) {
      this.handleCodesDelete(codeList);
    }
  }

  /**
   * Event launched when a code is deleted triggered with click on code chip x
   * @param ev
   */
  handleCodeDelete(ev: React.MouseEvent<HTMLInputElement>) {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.props.editable) {
      const { chargeSession, codableTransaction } = this.props;
      const element = ev.target as HTMLElement;
      const dataItem = getChipDataItem(
        element,
        null,
        codableTransaction || chargeSession
      ) as CMxCommonApp.ChargeSessionCode;
      const changeDetail: CMxCommonApp.CodeChange = {
        codes: [dataItem],
        context: {
          chargeSession: { ...this.props.chargeSession },
          transaction: { ...this.props.codableTransaction },
          invoice: { ...this.props.invoice },
          processInstance: { ...this.props.processInstance }
        }
      };
      this.props.handleCodeRemove(changeDetail);
      if (this.lookupRef.current) {
        this.lookupRef.current.focus();
      }
    }
  }

  /**
   * Event launched when a multiple code are deleted
   * @param ev
   */
  handleCodesDelete(codes: CMxCommonApp.CodeInfo[]) {
    if (this.props.editable) {
      const { chargeSession, codableTransaction } = this.props;
      const dataItems =
        codes?.map(
          (codeInfo) =>
            getChipDataItem(
              null,
              codeInfo,
              codableTransaction || chargeSession
            ) as CMxCommonApp.ChargeSessionCode
        ) ?? [];
      const changeDetail: CMxCommonApp.CodeChange = {
        codes: dataItems,
        context: {
          chargeSession: { ...this.props.chargeSession },
          transaction: { ...this.props.codableTransaction },
          invoice: { ...this.props.invoice },
          processInstance: { ...this.props.processInstance }
        }
      };
      if (dataItems) this.props.handleRemoveAllCodes(changeDetail);
      if (this.lookupRef.current) {
        this.lookupRef.current.focus();
      }
    }
  }

  /**
   * Event launched when a shared code is toggled.
   * @param ev
   */
  handleCodeChecked(ev: React.ChangeEvent<HTMLInputElement>) {
    ev.stopPropagation();
    this.dispatchSharedDxToggle(
      ev.target.name,
      ev.target.checked ? 'ADD' : 'REMOVE'
    );
  }

  /**
   * Event launched when a units are changed
   * @param ev
   */
  handleUnitsChange(units: number, unitsElement: HTMLElement) {
    if (this.props.editable) {
      const { codableTransaction } = this.props;

      const element = unitsElement as HTMLElement;

      const dataItem = getDataItem(
        element,
        codableTransaction
      ) as CMxCommonApp.ChargeSessionCode;
      if (units && isAValidUnits(units.toString())) {
        this.currentUnit = parseUnits(parseInt(units.toString()));
        const code = {
          ...dataItem,
          units: this.currentUnit
        } as CMxCommonApp.ChargeSessionCode;

        const changeDetail: CMxCommonApp.CodeChange = {
          codes: [code],
          context: {
            chargeSession: { ...this.props.chargeSession },
            transaction: { ...this.props.codableTransaction },
            invoice: { ...this.props.invoice },
            processInstance: { ...this.props.processInstance }
          }
        };

        this.props.handleCodeChange(changeDetail);
      } else {
        this.currentUnit = dataItem?.units ? Number(dataItem?.units) : null;
      }
    }
  }

  /**
   * Event launched when a transaction is re-ordered
   * @param ev
   */
  handleTransactionReorder(
    transactionCurrentIndex: number,
    transactionDestinationIndex: number
  ) {
    const transactions = this.props.invoice?.transactions.slice();
    const movedTransaction = transactions[transactionCurrentIndex];
    transactions.splice(transactionCurrentIndex, 1);
    transactions.splice(transactionDestinationIndex, 0, movedTransaction);

    const reorderedTransactions = transactions.map((txn, index) => ({
      guid: txn.guid,
      sequence_number: index + 1
    }));
    const transactionReorderDetail: CMxCommonApp.TransactionReorderDetail = {
      transactions: reorderedTransactions,
      context: {
        invoice: { ...this.props.invoice },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleTransactionOrderChange(transactionReorderDetail);
  }

  /**
   * Event launched when a transaction is deleted
   * @param event
   */
  onDelete(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    this.props.onChargeSessionAction({
      action: 'DELETE_TRANSACTION',
      chargeSession: this.props.chargeSession,
      meta: {
        invoice: this.props.invoice,
        transaction: { ...this.props.codableTransaction }
      }
    });
  }

  /**
   * Event launched by the accordion
   * @param action
   * @param item
   */
  onAccordionAction(action: any, item: any) {
    const chargeSessionCode = item.data as CMxCommonApp.ChargeSessionCode;
    const opening = action === AccordionSectionAction.OPEN;

    const selection: CMxCommonApp.ChargeSessionSelection = {
      chargeSessionCode,
      evidence: chargeSessionCode.evidence,
      selectionState: {
        active: false,
        hover: false,
        visible: opening
      }
    };
    this.props.onFeatureSelection(selection);
  }

  /**
   * Method used for enable a procedure with tech/pro charge
   * @param techTransactionCodable
   */
  handleEnableTechPro(
    techTransactionCodable: CMxCommonApp.TechCodableTransaction
  ) {
    if (this.props.editable) {
      techTransactionCodable.invoice = this.props.invoice;
      this.props.handleEnableTechPro(techTransactionCodable);
    }
  }

  /**
   * Method used for disable a procedure with tech/pro charge
   * @param techTransactionCodable
   */
  handleDisableTechPro(
    techTransactionCodable: CMxCommonApp.TechCodableTransaction
  ) {
    if (this.props.editable) {
      techTransactionCodable.invoice = this.props.invoice;
      this.props.handleDisableTechPro(techTransactionCodable);
    }
  }

  isTimeBasedCode = () => {
    return this.props.codableTransaction?.procedure?.time_based;
  };

  handleTimeBasedCodeAdd = (
    ev: React.MouseEvent<HTMLElement>,
    time: string,
    age: string
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    const { chargeSession, codableTransaction } = this.props;
    const element = ev.target as HTMLElement;
    const dataItem = getDataItem(
      element,
      codableTransaction || chargeSession
    ) as CMxCommonApp.ChargeSessionCode;
    const changeDetail: CMxCommonApp.CodeChange = {
      codes: [dataItem],
      context: {
        chargeSession: { ...this.props.chargeSession },
        transaction: { ...this.props.codableTransaction },
        invoice: { ...this.props.invoice },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleTimeBasedCodeAdd(changeDetail, time, age);
    if (this.lookupRef.current) {
      this.lookupRef.current.focus();
    }
  };

  handleTimeBasedCodeUpdate = (
    ev: React.MouseEvent<HTMLInputElement>,
    time: string,
    age: string
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    const { chargeSession, codableTransaction } = this.props;
    const element = ev.target as HTMLElement;
    const dataItem = getDataItem(
      element,
      codableTransaction || chargeSession
    ) as CMxCommonApp.ChargeSessionCode;
    const changeDetail: CMxCommonApp.CodeChange = {
      codes: [dataItem],
      context: {
        chargeSession: { ...this.props.chargeSession },
        transaction: { ...this.props.codableTransaction },
        invoice: { ...this.props.invoice },
        processInstance: { ...this.props.processInstance }
      }
    };
    this.props.handleTimeBasedCodeUpdate(changeDetail, time, age);
    if (this.lookupRef.current) {
      this.lookupRef.current.focus();
    }
  };

  isClockIconDisabled = () => {
    const procedure = this.props.codableTransaction?.procedure;
    const invoice = this.props.invoice;
    return (
      procedure &&
      invoice?.transactions?.some(
        (transaction) =>
          transaction?.procedure?.add_on_code_id ===
          procedure?.additionalProperties['id']
      )
    );
  };

  //handle this manually so we can unmount it
  handlePopoverClose = (newState: boolean) => {
    this.setState({ clickTriggered: newState });
  };

  timeBasedCodePopup = () => {
    // Only allow the popover to be open if the trigger is in view
    const popoverState =
      this.state.clickTriggered && this.state.isTriggerInView;

    const handlePopoverOpen = (ev: React.MouseEvent<HTMLElement>) => {
      // Prevents the default behavior of the click event
      ev.stopPropagation();

      // Toggle the state when the user clicks to open the popover manually
      this.setState({ clickTriggered: !this.state.clickTriggered });
    };

    return (
      <Popover
        isOpen={popoverState}
        placement="bottom-end"
        variant="time"
        trigger="click"
        flip={true}
        preventOverflow={false}
        boundary={this.accordionPanelRef?.current || 'scrollParent'}>

              {/* @ts-ignore */}
        <PopoverTrigger>
          <Button
            ref={this.triggerRef}
            aria-label=""
            variant="clock"
            disabled={!this.props.editable || this.isClockIconDisabled()}
            onClick={handlePopoverOpen}>
            <CMXIcon
              svgIconName="BiomeClock"
              svgIconSize="20"
              svgIconClass={
                this.props.codableTransaction?.procedure?.time_based &&
                this.props.codableTransaction?.procedure?.total_time
                  ? 'grey-clock'
                  : 'color-clock'
              }
            />
            {this.props.codableTransaction?.procedure?.time_based &&
              this.props.codableTransaction?.procedure?.total_time && (
                <div className="total-time">
                  {this.props.codableTransaction.procedure.total_time}
                </div>
              )}
          </Button>
        </PopoverTrigger>
        <Portal>
          {popoverState && (
            <PopoverContent>
              <PopoverArrow />
              <PopoverBody>
                <TimeBasedCodePopover
                  codableTransaction={this.props.codableTransaction}
                  handleTimeBasedCodeAdd={this.handleTimeBasedCodeAdd}
                  handleTimeBasedCodeUpdate={this.handleTimeBasedCodeUpdate}
                  processInstance={this.props.processInstance}
                  popoverState={popoverState}
                  updatePopoverState={this.handlePopoverClose}
                />
              </PopoverBody>
            </PopoverContent>
          )}
        </Portal>
      </Popover>
    );
  };
  toggleAccordion = () => {
    this.setState((prevState) => ({
      isTransactionRowOpen: !prevState.isTransactionRowOpen
    }));
  };

  render() {
    const {
      chargeSession,
      codableTransaction,
      lineNumber,
      editable,
      invoice,
      onCodeSearch,
      handleCodeChange,
      handleCodeSearchDrop,
      handleCodeRemove,
      canDropOnto,
      searchResults,
      codeSearchResultsV2,
      onCodeSearchSelection,
      onFeatureSelection,
      onChargeSessionAction,
      onEdit,
      autoFocus,
      panelActivations,
      processInstance,
      transaction,
      debounceTimeout,
      codeEdits,
      suggestedCodes,
      onTransactionSharedDiagnosisChange,
      transactionIndex,
      ...rest
    } = this.props;
    const txnType = transaction?.type ?? 'unknown txn type';

    const styleClasses: any = [
      'charge-session-informationV2',
      `charge-session-${txnType.toLowerCase()}`,
      {
        'charge-session-editable': editable,
        'charge-session-uneditable': !editable,
        'charge-session-drop-zone': this.state.dragHover
      }
    ];

    const boundTransactionAccordionBuilder = transactionAccordianBuilderV2.bind(
      null,
      this.handleUnitsChange,
      this.handleTransactionReorder,
      this.handleCodeDelete,
      this.handleCodeChecked,
      this.handleEnableTechPro,
      this.handleDisableTechPro,
      this.isTimeBasedCode,
      this.timeBasedCodePopup,
      this.onSearch,
      this.onSearchSelection,
      this.onAddAllCodes,
      this.onRemoveAllCodes,
      this.codeDropHandler,
      this.onDropHandler,
      this.onAccordionAction,
      this.onDelete,
      suggestedCodes,
      codeSearchResultsV2,
      invoice,
      codeEdits,
      // disable editable actions(i,e add diagnosis,modifiers) on a temporary transaction
      this.props.editable && !codableTransaction.isTemporary,
      autoFocus,
      transactionIndex,
      this.props.codeSourcesLedgerEnabled
    );

    const boundMedicalCodeAccordionBuilder = medicalCodeAccordianBuilder.bind(
      null,
      onFeatureSelection,
      panelActivations,
      invoice,
      codeEdits
    );

    const assuranceErrorMessage = codableTransaction?.procedure?.errorMessages
      ? codableTransaction?.procedure?.errorMessages
      : [];

    const sharedCodes = invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES;
    const boundMedicalCodeAccordionBuilderHandler = (
      code: CMxCommonApp.ChargeSessionCode | CMxCommonApp.ZippedCodeBlock,
      index: number
    ) => {
      return boundMedicalCodeAccordionBuilder(
        code,
        index,
        codableTransaction,
        assuranceErrorMessage
      );
    };
    const boundTransactionAccordionBuilderHandler = (items: any) => {
      return boundTransactionAccordionBuilder(
        codableTransaction,
        transaction,
        assuranceErrorMessage,
        items
      );
    };

    let transactionAccordionItem;
    let chargeMeta: any = false;

    let items = [];
    let highlightTransaction = false;
    if (codableTransaction) {
      let codes = JSON.parse(JSON.stringify(codableTransaction.codes ?? []));

      if (!sharedCodes) {
        const onlyShared = codes.filter(sharedCodePredicate);
        const firstIndex = codes.findIndex(sharedCodePredicate);
        const lastIndex = codes.findIndex(notSharedCodePredicate, firstIndex);
        const preArray = codes.slice(0, firstIndex);
        const postArray = codes.slice(firstIndex + onlyShared.length);

        const zippedShareCodes = {
          codes: onlyShared,
          codeType: commonEnums.CodeType.DIAGNOSIS,
          zipped: true
        };
        codes = [...preArray, zippedShareCodes, ...postArray];
      }

      const filtered = codes.filter(
        (code) =>
          cmxTypeguards.isChargeSessionCode(code) ||
          (cmxTypeguards.isZippedCodeBlock(code) && code.codes.length)
      );
      items = filtered.map(boundMedicalCodeAccordionBuilderHandler);

      transactionAccordionItem = boundTransactionAccordionBuilderHandler(items);
      const txnLedger = getLedgerTransaction(
        getLedgerTransactionsFromInvoice(invoice),
        codableTransaction?.guid);
      highlightTransaction = shouldHighlightTransaction(txnLedger) &&
        !!this.props.codeSourcesLedgerEnabled;
    }
    // else if (chargeSession) {
    //   items = chargeSession.codes.map(boundAccordionBuilderHandler) as any[];

    //   // TODO: Determine code types
    //   chargeMeta = (
    //     <div className="charge-line">
    //       <span className="charge-index">Charge {lineNumber}:</span>
    //       <span className="charge-charge-type" style={{ display: 'none' }}>
    //         SOME CODE TYPE:
    //       </span>
    //       {editable ? (
    //         <span className="charge-delete-container">
    //           <a href="#/delete-session" onClick={this.onDelete}>
    //             remove
    //           </a>
    //         </span>
    //       ) : (
    //         false
    //       )}
    //     </div>
    //   );
    // }
    else if (transaction) {
      const diagnosis = [];
      const modifiers = [];
      // TODO:
      // const diagnosis = (invoice?.shared_codes.diagnoses ?? []).map(
      //   code => code.diagnosis_code
      // );
      // const modifiers = (invoice?.shared_codes.modifiers ?? []).map(
      //   code => code.modifier_code
      // );
      const dxString = diagnosis.length
        ? `diagnosis: ${diagnosis.join(', ')}`
        : '';
      const modString = modifiers.length
        ? `modifiers: ${modifiers.join(', ')}`
        : '';
      const conjunction = diagnosis.length && modifiers.length ? ' and ' : '';
      const alsoIncludes = `${dxString} ${conjunction} ${modString}`;
      styleClasses.push({
        'show-also-includes': diagnosis.length || modifiers.length
      });

      const label =
        txnType in CHARGE_TYPE_LABEL ? CHARGE_TYPE_LABEL[txnType] : txnType;

      items = transaction.codes.map(
        boundMedicalCodeAccordionBuilderHandler
      ) as any[];
      transactionAccordionItem = boundTransactionAccordionBuilderHandler(items);
      chargeMeta = (
        <div
          className={`transaction-meta transaction-${txnType.toLowerCase()}`}>
          <span className="ordinal">{lineNumber}. </span>
          {label}{' '}
          <span className="also-includes">Includes shared {alsoIncludes}</span>
        </div>
      );
    }

    return (
    <div className={classnames(styleClasses, {"transaction-ledger-highlight": highlightTransaction})}
      key={transactionIndex} >
        {/* Charge Header Line */}
        {chargeMeta}
        <div className="code-section">
          {transactionAccordionItem ? (
            <Accordion
              allowToggle
              className={`transaction-accordion-section ${
                this.state.isTransactionRowOpen
                  ? 'transaction-row-expanded'
                  : ''
              }`}>
              <AccordionItem>
                <div className="accordion-header-section">
                  <div
                    className={'accordion-header-content'}
                    onClick={(e) => e.stopPropagation()} // Prevents toggling when clicking on text
                  >
                    {transactionAccordionItem.header}
                  </div>

                  <AccordionButton
                    as="div"
                    className="accordion-header-toggle"
                    onClick={this.toggleAccordion}>
                    <AccordionIcon />
                  </AccordionButton>
                </div>

                {/* Accordion Content */}
                <AccordionPanel
                  className="accordion-panel-section"
                  ref={this.accordionPanelRef}>
                  {transactionAccordionItem.body}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          ) : editable ? (
            <div className="empty-message editable">
              Add codes using the search box below.
            </div>
          ) : (
            <div className="empty-message uneditable">No codes selected</div>
          )}
        </div>
        {/* Transaction Error Message */}
        <ErrorSubmission
          errorMessages={codableTransaction?.errorMessages}
          messageStyle="txn-error-message"
          messageType="error"
        />

        {/* Procedure Error Message */}
        <ErrorSubmission
          errorMessages={codableTransaction?.procedure?.errorMessages}
          messageStyle="procedure-error-message"
          messageType="error"
        />
      </div>
    );
  }
}

export { TransactionV2 };
